if (!customElements.get("product-card")) {
	customElements.define(
		"product-card",
		class ProductCard extends HTMLDivElement {
			connectedCallback() {
				if (this.initialized) return;

				this.variantsDataEl = this.querySelector("[data-product-variants]");
				this.buyBoxForm = this.querySelector(".product-card__buy-box-form");

				if (!this.variantsDataEl) return;

				try {
					this.variants = JSON.parse(this.variantsDataEl.textContent);
				} catch (error) {
					console.error("Unable to parse product card variants.", error);
					return;
				}

				this.initialized = true;
				this.colorOptionIndex = this.dataset.colorOptionIndex;
				this.inStockTemplate = this.dataset.inStockTemplate || "In Stock: [[count]]";
				this.outOfStockLabel = this.dataset.outOfStockLabel || "Out of stock";
				this.soldOutLabel = this.dataset.soldOutLabel || "Sold out";

				this.querySelectorAll(
					".product-card__swatches .swatch-item[data-variant-id]",
				).forEach((item) => {
					item.querySelector("button")?.addEventListener("click", (event) => {
						event.preventDefault();
						this.selectVariant(Number(item.dataset.variantId), item);
					});
				});
			}

			selectVariant(variantId, swatchItem) {
				const variant = this.variants.find((entry) => entry.id === variantId);
				if (!variant) return;

				this.querySelectorAll(".product-card__swatches .swatch-item").forEach((item) => {
					item.removeAttribute("aria-selected");
					item.querySelector("button")?.setAttribute("aria-pressed", "false");
				});
				swatchItem?.setAttribute("aria-selected", "true");
				swatchItem?.querySelector("button")?.setAttribute("aria-pressed", "true");

				const variantInput = this.buyBoxForm?.querySelector(".product-variant-id");
				if (variantInput) {
					variantInput.value = variant.id;
					variantInput.disabled = !variant.available;
				}

				const skuEl = this.querySelector(".product-card__sku");
				if (skuEl) {
					skuEl.textContent = variant.sku || "";
					skuEl.hidden = !variant.sku;
				}

				const upcWrapper = this.querySelector(".product-card__upc");
				const upcEl = this.querySelector(".product-card__upc-value");
				if (upcEl) upcEl.textContent = variant.barcode || "";
				if (upcWrapper) upcWrapper.hidden = !variant.barcode;

				const labelEl = this.querySelector(".product-card__swatch-label");
				if (labelEl && swatchItem?.dataset.swatchValue) {
					labelEl.textContent = swatchItem.dataset.swatchValue;
				} else if (labelEl && this.colorOptionIndex !== undefined) {
					const optionKey = `option${Number(this.colorOptionIndex) + 1}`;
					labelEl.textContent = variant[optionKey] || "";
				}

				this.#updateStockStatus(variant);
				this.#updatePrice(variant);
				this.#updateQuantityInput(variant);
				this.#updateAtcButton(variant);
				this.#updateImage(variant, swatchItem);
			}

			#updateStockStatus(variant) {
				const stockEl = this.querySelector(".product-card__stock-status");
				if (!stockEl) return;
				const inventoryQuantity = Number(variant.inventory_quantity) || 0;
				const isInStock = inventoryQuantity > 0;

				stockEl.classList.toggle("product-card__stock-status--in-stock", isInStock);
				stockEl.classList.toggle(
					"product-card__stock-status--out-of-stock",
					!isInStock,
				);

				const stockText =
					stockEl.querySelector(".product-card__stock-text") ||
					document.createElement("span");
				stockText.className = "product-card__stock-text";
				stockText.textContent = isInStock
					? this.inStockTemplate.replace("[[count]]", inventoryQuantity)
					: this.outOfStockLabel;

				if (!stockText.parentNode) stockEl.append(stockText);
			}

			#updatePrice(variant) {
				const priceEl = this.querySelector(".product-card__price-stock .f-price");
				if (!priceEl || !window.FoxTheme?.Currency) return;

				const moneyFormat = FoxTheme.settings.moneyFormat;
				const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

				priceEl.classList.toggle("f-price--on-sale", Boolean(onSale));
				priceEl.classList.toggle("f-price--sold-out", !variant.available);

				const regularPriceEl = priceEl.querySelector(
					".f-price__regular .f-price-item--regular",
				);
				const salePriceEl = priceEl.querySelector(".f-price__sale .f-price-item--sale");
				const compareAtPriceEl = priceEl.querySelector(
					".f-price__sale .f-price-item--regular s",
				);

				if (onSale) {
					if (salePriceEl) {
						salePriceEl.textContent = FoxTheme.Currency.formatMoney(
							variant.price,
							moneyFormat,
						);
					}
					if (compareAtPriceEl) {
						compareAtPriceEl.textContent = FoxTheme.Currency.formatMoney(
							variant.compare_at_price,
							moneyFormat,
						);
					}
				} else if (regularPriceEl) {
					regularPriceEl.textContent = FoxTheme.Currency.formatMoney(
						variant.price,
						moneyFormat,
					);
					if (salePriceEl) salePriceEl.textContent = "";
					if (compareAtPriceEl) compareAtPriceEl.textContent = "";
				}
			}

			#updateQuantityInput(variant) {
				const quantityInput = this.buyBoxForm?.querySelector(".quantity__input");
				if (!quantityInput || !variant.quantity_rule) return;

				const { min, max, increment } = variant.quantity_rule;
				const minValue = min ?? 1;
				const stepValue = increment ?? 1;

				quantityInput.step = stepValue;
				quantityInput.min = minValue;
				quantityInput.value = minValue;
				quantityInput.dataset.quantityVariantId = variant.id;
				quantityInput.dataset.min = minValue;

				if (max != null) {
					quantityInput.max = max;
					quantityInput.dataset.max = max;
				} else {
					quantityInput.removeAttribute("max");
					delete quantityInput.dataset.max;
				}
			}

			#updateAtcButton(variant) {
				const atcButton = this.buyBoxForm?.querySelector(".product-card__buy-box-atc");
				const atcText = atcButton?.querySelector(".btn__text");
				if (!atcButton || !atcText) return;

				const canAddToCart = variant.available;
				atcButton.disabled = !canAddToCart;
				atcButton.setAttribute(
					"aria-label",
					canAddToCart ? atcButton.dataset.addToCartLabel : this.outOfStockLabel,
				);
				atcText.textContent = canAddToCart
					? atcButton.dataset.addToCartLabel
					: this.outOfStockLabel;
			}

			#updateImage(variant, swatchItem) {
				const mainImage = this.querySelector(".product-card__image--main img");
				if (!mainImage) return;

				const swatchImage = swatchItem?.querySelector(".color-swatch--variant-image");
				if (swatchImage?.src) {
					mainImage.src = swatchImage.currentSrc || swatchImage.src;
					if (swatchImage.srcset) mainImage.srcset = swatchImage.srcset;
					if (swatchImage.alt) mainImage.alt = swatchImage.alt;
					return;
				}

				if (variant.image) {
					mainImage.src = variant.image;
					mainImage.removeAttribute("srcset");
				}
			}
		},
		{ extends: "div" },
	);
}
